/**
 * @file Parameter validation with safe fallbacks and dev-only warnings.
 *
 * Content tables are hand-authored data, so a typo or an out-of-range value is
 * a real possibility. Rather than crash — or worse, silently misbehave — the
 * constructors that consume that data run each field through these helpers: an
 * invalid value is replaced by a known-safe fallback, and a descriptive warning
 * is printed in development builds only (production stays quiet and cheap).
 *
 * Every warning names the instance, the offending parameter, the value that was
 * received and the fallback that was substituted, which is exactly what the
 * `enemy_suicide_bomber` spec (issue #5) asks the validator to report.
 */

/** True in Vite dev builds; guards every warning so production stays silent. */
export const IS_DEV = Boolean(import.meta.env?.DEV);

/**
 * Emits a parameter warning in development builds.
 * @param {string} instance Human-readable owner (e.g. an archetype name).
 * @param {string} param Parameter name that failed validation.
 * @param {*} received The value that was supplied.
 * @param {*} fallback The value used instead.
 * @param {string} [reason] Extra context appended to the message.
 * @returns {void}
 */
export function warnParam(instance, param, received, fallback, reason = '') {
  if (!IS_DEV) return;
  const tail = reason ? ` (${reason})` : '';
  // eslint-disable-next-line no-console
  console.warn(
    `[${instance}] invalid "${param}": received ${JSON.stringify(received)}, ` +
      `using fallback ${JSON.stringify(fallback)}${tail}.`
  );
}

/**
 * Returns `value` when it is a finite number satisfying `predicate`, otherwise
 * `fallback` (with a dev warning).
 * @param {string} instance Owner name for warnings.
 * @param {string} param Parameter name.
 * @param {*} value Supplied value.
 * @param {number} fallback Safe value used when invalid.
 * @param {(n: number) => boolean} [predicate] Extra constraint on the number.
 * @param {string} [reason] Constraint description, used in the warning.
 * @returns {number}
 */
export function number(instance, param, value, fallback, predicate, reason) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    if (value !== undefined) warnParam(instance, param, value, fallback, 'not a finite number');
    return fallback;
  }
  if (predicate && !predicate(value)) {
    warnParam(instance, param, value, fallback, reason);
    return fallback;
  }
  return value;
}

/** Convenience: a finite number `>= 0`. */
export function nonNegative(instance, param, value, fallback) {
  return number(instance, param, value, fallback, (n) => n >= 0, 'must be >= 0');
}

/** Convenience: a finite number `> 0`. */
export function positive(instance, param, value, fallback) {
  return number(instance, param, value, fallback, (n) => n > 0, 'must be > 0');
}

/**
 * Returns `value` when it is one of `allowed`, otherwise `fallback` (with a dev
 * warning). Used for enum-like fields such as `starter_state`.
 * @param {string} instance Owner name for warnings.
 * @param {string} param Parameter name.
 * @param {*} value Supplied value.
 * @param {ReadonlyArray<*>} allowed Permitted values.
 * @param {*} fallback Safe value used when invalid.
 * @returns {*}
 */
export function oneOf(instance, param, value, allowed, fallback) {
  if (allowed.includes(value)) return value;
  if (value !== undefined) warnParam(instance, param, value, fallback, `not one of ${allowed.join(', ')}`);
  return fallback;
}

/**
 * Coerces to a boolean, defaulting when absent. Non-boolean, non-undefined
 * values warn.
 * @param {string} instance Owner name for warnings.
 * @param {string} param Parameter name.
 * @param {*} value Supplied value.
 * @param {boolean} fallback Default value.
 * @returns {boolean}
 */
export function bool(instance, param, value, fallback) {
  if (value === undefined) return fallback;
  if (typeof value !== 'boolean') {
    warnParam(instance, param, value, fallback, 'not a boolean');
    return fallback;
  }
  return value;
}
